// Épico [AUDITORIA] Central de Entrada de Dados — contratos de dados da tela.
//
// DEVN3-662: estes tipos espelham as respostas de `api/auditoria-entrada-dados` (ver
// Ainumera.Core/DTOs/AuditoriaEntradaDadosDtos.cs). Validações, estados, percentual e permissões
// vêm sempre do backend — a tela não decide nem simula nenhum deles.
import { IExtratoPdfInfo } from '../../../../interfaces/IArquivoOperacao';

// ─── Tipo de Entrada (AUDIT_INPUT_TYPE) ────────────────────────────────────────────────────

export type CategoriaEntradaDados = 'BANKING' | string;

export interface IAuditInputType {
  code: string; // ex.: 'BANK_STATEMENT'
  name: string; // ex.: 'Extrato Bancário'
  category: CategoriaEntradaDados;
  description?: string;
  acceptedExtensions: string[]; // ex.: ['pdf', 'ofx']
  multipleFiles: boolean;
  requiresCompany: boolean;
  requiresAccount: boolean;
  requiresPeriod: boolean;
  enabled: boolean;
}

// ─── Conta Contábil (bancária) ──────────────────────────────────────────────────────────────

// Conta de banco ou de caixa do período selecionado — o `id` é o do cartão da conta.
export interface IContaContabilBancaria {
  id: number;
  codigo: string; // ex.: '1.01.01.001'
  descricao: string; // ex.: 'Banco Itaú Conta Corrente'
}

// ─── Status do arquivo (AUDIT_FILE.status) — seção 23 do Épico ─────────────────────────────

export type StatusArquivo =
  | 'PENDING_UPLOAD'
  | 'UPLOADING'
  | 'UPLOADED'
  | 'VALIDATING'
  | 'VALIDATION_WARNING'
  | 'VALIDATION_ERROR'
  | 'READY_TO_PROCESS'
  | 'PROCESSING'
  | 'PROCESSED'
  | 'REPROCESSING'
  | 'PROCESSING_ERROR'
  // PDF: a previsão de custo da leitura por IA está pronta e espera a decisão do usuário.
  | 'AWAITING_COST_DECISION'
  // PDF: a leitura não fechou com os saldos (ou não há saldo) e espera a confirmação do usuário.
  | 'AWAITING_CONFIRMATION'
  | 'CANCELLED'
  | 'REPLACED';

// Uma task do backend está (ou vai estar) trabalhando no arquivo — é enquanto a tela atualiza sozinha.
export const arquivoEmAndamento = (status: StatusArquivo) =>
  status === 'UPLOADED' || status === 'PROCESSING' || status === 'REPROCESSING';

// ─── Status do processamento (AUDIT_FILE_PROCESSING.status) — seção 31 do Épico ────────────

export type StatusProcessamento =
  | 'QUEUED'
  | 'STARTING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'RETRYING';

// ─── Validação (AUDIT_FILE_VALIDATION) — seção 25 do Épico ─────────────────────────────────

export type CodigoValidacao =
  | 'ACCOUNT_MATCH'
  | 'PERIOD_MATCH'
  | 'FILE_FORMAT'
  | 'FILE_DUPLICATE'
  | 'FILE_INTEGRITY'
  | 'FILE_SIZE';

export type TipoMensagem = 'INFO' | 'WARNING' | 'ALERT' | 'ERROR' | 'SUCCESS';

export interface IAuditFileValidation {
  id: string;
  validationCode: CodigoValidacao;
  validationType: TipoMensagem;
  status: 'OK' | 'WARNING' | 'ERROR';
  expectedValue?: string | null;
  detectedValue?: string | null;
  message: string;
  blocking: boolean;
  acknowledged?: boolean;
}

// ─── Processamento (AUDIT_FILE_PROCESSING) — seção 30 do Épico ─────────────────────────────

export interface IAuditFileProcessing {
  auditFileId: number;
  status: StatusProcessamento;
  currentStep: string;
  progressPercentage: number; // 0–100, vem do backend — o front-end nunca calcula/simula
  statusMessage: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  attemptNumber: number;
}

// ─── Arquivo (AUDIT_FILE) — seção 22 do Épico ──────────────────────────────────────────────

export interface IAuditFile {
  id: number;
  companyId: number;
  companyName: string;
  esteiraId?: number;
  auditInputTypeCode: string; // 'BANK_STATEMENT'
  accountingAccountId?: number; // id do cartão da conta
  accountingAccountLabel?: string; // "1.01.01.001 – Banco Itaú Conta Corrente"
  banco?: string | null; // banco/conta lidos do próprio arquivo, quando ele traz
  periodStart?: string; // ISO
  periodEnd?: string; // ISO
  originalFileName: string;
  extension: string;
  mimeType?: string | null;
  fileSize: number; // bytes
  fileHashSha256: string;
  status: StatusArquivo;
  validationStatus?: 'OK' | 'WARNING' | 'ERROR' | null;
  validations: IAuditFileValidation[];
  processing?: IAuditFileProcessing | null;
  uploadedByUserName: string;
  uploadedAt: string; // ISO
  versionNumber: number;
  // Ações permitidas calculadas pelo backend (seção 40/42 do Épico — "o Front-end não deverá
  // decidir sozinho se uma ação é permitida").
  permissions: {
    canDelete: boolean;
    canReplace: boolean;
    canReprocess: boolean;
    canAddNew: boolean;
    canViewResult?: boolean;
  };
  // Só para PDF: previsão de custo da leitura por IA, andamento e conferência de saldos.
  pdf?: IExtratoPdfInfo | null;
}

// ─── Trilha de Auditoria (AUDIT_TRAIL_EVENT) — seção 36/37 do Épico ────────────────────────

export type OrigemEvento = 'USER' | 'SYSTEM' | 'API' | 'PROCESS';

export type TipoEventoTrilha =
  | 'FILE_SELECTED'
  | 'FILE_UPLOAD_STARTED'
  | 'FILE_UPLOADED'
  | 'FILE_UPLOAD_FAILED'
  | 'FILE_VALIDATED'
  | 'FILE_REJECTED'
  | 'FILE_REPLACED'
  | 'FILE_DELETED'
  | 'FILE_REPROCESSED'
  | 'ACCOUNT_LINKED'
  | 'ACCOUNT_CHANGED'
  | 'PERIOD_DEFINED'
  | 'PERIOD_CHANGED'
  | 'VALIDATION_WARNING'
  | 'VALIDATION_ERROR'
  | 'WARNING_ACCEPTED'
  | 'DUPLICATE_DETECTED'
  | 'PROCESS_QUEUED'
  | 'PROCESS_STARTED'
  | 'PROCESS_STEP_CHANGED'
  | 'PROCESS_COMPLETED'
  | 'PROCESS_FAILED'
  | 'PROCESS_RETRY_REQUESTED';

export interface IAuditTrailEvent {
  id: number;
  auditFileId: number;
  eventType: TipoEventoTrilha;
  userName?: string | null;
  source: OrigemEvento;
  previousStatus?: string | null;
  newStatus?: string | null;
  description: string;
  createdAt: string; // ISO
}

// ─── Contexto da importação ─────────────────────────────────────────────────────────────────

// O que identifica onde o arquivo entra: o período (esteira) e a conta (cartão). Empresa, rótulo
// da conta e datas do período saem do backend a partir desses dois.
export interface IImportacaoContexto {
  esteiraId: number;
  cartaoId: number;
  auditInputTypeCode: string;
}

export type AcaoConfirmarPdf = 'IMPORTAR' | 'RELER' | 'DESCARTAR';

// ─── Resultado do cruzamento extrato × contabilidade (por conta) ───────────────────────────

export interface IAuditoriaExtratoResumo {
  esteiraId: number;
  cartaoId: number;
  contaLabel: string;
  arquivosConsiderados: number;
  extratoTotal: number;
  extratoValorTotal: number;
  extratoConciliados: number;
  extratoValorConciliado: number;
  extratoPendentes: number;
  extratoValorPendente: number;
  contabilidadeTotal: number;
  contabilidadeValorTotal: number;
  contabilidadeConciliados: number;
  contabilidadeValorConciliado: number;
  contabilidadePendentes: number;
  contabilidadeValorPendente: number;
  grupos: number;
  percentualConciliado: number; // 0–100, vem do backend
}

export interface IAuditoriaExtratoLinha {
  id: number;
  data?: string | null;
  historico?: string | null;
  valor: number;
  tipo: string; // 'C' | 'D'
  arquivo?: string | null;
}

export interface IAuditoriaExtratoGrupo {
  chave: string;
  data?: string | null;
  valor: number;
  extrato: IAuditoriaExtratoLinha[];
  contabilidade: IAuditoriaExtratoLinha[];
}

export interface IAuditoriaExtratoPagina<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
}
